/**
 * PipelineDetailTabs — tabbed container for a selected pipeline's detail
 * view.
 *
 * Tabs:
 *   Pipeline Overview — metadata + a simple source→jobs→target flow diagram
 *   Jobs               — create/edit/run-all/delete-all jobs (JobList)
 *
 * "Pipeline Runs" (run history + per-job progress) lives at the top level
 * now, next to "Scheduler" (see PipelineRunsDashboard in index.tsx) — it's
 * no longer nested under a specific pipeline's tabs, and the old separate
 * "Pipeline Details" tab was merged into it.
 */

import { useState } from "react";
import { LayoutGrid, List } from "lucide-react";
import { JobList } from "./JobList";
import { PipelineOverviewTab } from "./PipelineOverviewTab";
import type { PEJob, PEPipeline } from "../../features/pipeline-engineer/types";

type DetailTab = "overview" | "jobs";

const TABS: Array<{ key: DetailTab; label: string; icon: typeof LayoutGrid }> =
  [
    { key: "overview", label: "Pipeline Overview", icon: LayoutGrid },
    { key: "jobs", label: "Jobs", icon: List },
  ];

interface PipelineDetailTabsProps {
  pipeline: PEPipeline;
  projectId: string;
  refreshKey?: number;
  selectedJobId?: string;
  onSelectJob: (job: PEJob) => void;
  onJobCreated: (job: PEJob) => void;
}

export function PipelineDetailTabs({
  pipeline,
  projectId,
  refreshKey = 0,
  selectedJobId,
  onSelectJob,
  onJobCreated,
}: PipelineDetailTabsProps) {
  const [tab, setTab] = useState<DetailTab>("overview");

  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-1 border-b border-white/10 overflow-x-auto"
        role="tablist"
        aria-label="Pipeline detail tabs"
      >
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            onClick={() => setTab(key)}
            aria-selected={tab === key}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === key
                ? "border-brand-500 text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            <Icon size={13} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <PipelineOverviewTab pipeline={pipeline} refreshKey={refreshKey} />
      )}

      {tab === "jobs" && (
        <JobList
          pipelineId={pipeline.id}
          projectId={projectId}
          refreshKey={refreshKey}
          selectedJobId={selectedJobId}
          onSelectJob={onSelectJob}
          onJobCreated={onJobCreated}
        />
      )}
    </div>
  );
}
